// ! code copied from https://github.com/brothatru/react-video-thumbnail
import { useState, useRef, useEffect, useCallback } from 'react';

type ThumbnailProps = {
  snapshot: string;
};

const ThumbnailImage: React.FC<ThumbnailProps> = ({ snapshot }) => (
  <div className="react-thumbnail-generator">
    <img src={snapshot} alt="my video thumbnail" />
  </div>
);

type ImageFormat = 'image/png' | 'image/jpeg' | 'image/webp';

type VideoThumbnailProps = {
  videoUrl: string;
  snapshotAtTime: number;
  cors: boolean;
  width?: number;
  height?: number;
  renderThumbnail?: boolean;
  thumbnailHandler?:
    | (({ src, timestamp }: { src: string; timestamp: number }) => void)
    | undefined;
  format?: ImageFormat;
};

export const VideoThumbnail: React.FC<VideoThumbnailProps> = ({
  cors = false,
  width,
  height,
  renderThumbnail = true,
  snapshotAtTime = 2,
  thumbnailHandler,
  videoUrl,
  format = 'image/jpeg',
}) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [metadataLoaded, setMetadataLoaded] = useState(false);
  const [seeked, setSeeked] = useState(false);
  const [snapshot, setSnapshot] = useState<string | false>(false);
  const [suspended, setSuspended] = useState(false);
  const canGetSnapshot = !snapshot && metadataLoaded && dataLoaded && suspended;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const thumbnailHandlerRef = useRef<
    VideoThumbnailProps['thumbnailHandler'] | null
  >(null);
  thumbnailHandlerRef.current = thumbnailHandler;

  const getSnapshot = useCallback(
    (snapshotAtTime: number) => {
      try {
        if (videoRef.current && canvasRef.current) {
          const video = videoRef.current;
          const canvas = canvasRef.current;

          canvas.height = height ?? video.videoHeight;
          canvas.width = width ?? video.videoWidth;

          if (!width || !height) {
            canvas.getContext('2d')?.drawImage(video, 0, 0);
          } else {
            canvas.getContext('2d')?.drawImage(video, 0, 0, width, height);
          }

          const thumbnail = canvas.toDataURL(format);

          video.src = '';
          if (video.parentElement) {
            video.parentElement.removeChild(video);
          }

          if (canvas.parentElement) {
            canvas.parentElement.removeChild(canvas);
          }

          setSnapshot(thumbnail);
          thumbnailHandlerRef.current?.({
            src: thumbnail,
            timestamp: snapshotAtTime,
          });
        }
      } catch (e) {
        console.error(e);
      }
    },
    [height, width, format],
  );

  useEffect(() => {
    if (canGetSnapshot) {
      if (
        videoRef.current &&
        (!videoRef.current?.currentTime ||
          videoRef.current.currentTime < snapshotAtTime)
      ) {
        videoRef.current.currentTime = snapshotAtTime;
      }

      if (seeked && !snapshot) {
        getSnapshot(snapshotAtTime);
      }
    }
  }, [seeked, snapshotAtTime, getSnapshot, canGetSnapshot, snapshot]);

  return (
    <div>
      {!snapshot ? (
        <div>
          <canvas
            className="block h-[1px] w-[1px] z-1 left-0 top-0 object-contain fixed"
            ref={canvasRef}
          />
          <video
            muted
            className="snapshot-generator"
            ref={videoRef}
            src={videoUrl}
            crossOrigin={!cors ? 'anonymous' : undefined}
            onLoadedMetadata={() => setMetadataLoaded(true)}
            onLoadedData={() => setDataLoaded(true)}
            onSuspend={() => setSuspended(true)}
            onSeeked={() => setSeeked(true)}
          />
        </div>
      ) : renderThumbnail ? (
        <ThumbnailImage snapshot={snapshot} />
      ) : null}
    </div>
  );
};
